import React, { useState, useEffect } from 'react';
import { apiService } from '../services/api';
import { toast } from 'react-toastify';

const EmailTemplates = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await apiService.getEmailTemplates();
      if (response.data && response.data.success) {
        // Handle nested response structure
        const templates = response.data.data?.templates || [];
        setTemplates(templates);
      } else if (response.data?.templates) {
        // Handle direct response structure
        setTemplates(response.data.templates);
      } else {
        // Fallback to empty array
        setTemplates([]);
      }
    } catch (error) {
      console.error('Failed to load email templates:', error);
      toast.error('Failed to load email templates');
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="loading-spinner mr-2"></div>
        <span>Loading templates...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6 fade-in mt-20 ">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Email Templates</h1>
          <p className="text-gray-500">Manage your email templates for different warmup phases</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="btn-primary flex items-center space-x-2"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          <span>Create Template</span>
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {templates.map((template) => (
          <div key={template.id} className="bg-white rounded-lg p-6 card-shadow">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">{template.name}</h3>
                <div className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  template.template_type === 'warmup' ? 'bg-blue-100 text-blue-800' :
                  template.template_type === 'marketing' ? 'bg-green-100 text-green-800' :
                  'bg-gray-100 text-gray-800'
                }`}>
                  {template.template_type}
                </div>
              </div>
              <div className="text-right">
                <div className="text-sm text-gray-500">Max Emails</div>
                <div className="font-semibold">{template.max_emails}</div>
              </div>
            </div>
            
            <div className="space-y-2">
              <div>
                <div className="text-xs font-medium text-gray-500 uppercase">Subject</div>
                <div className="text-sm text-gray-900 truncate">{template.subject_template}</div>
              </div>
              <div>
                <div className="text-xs font-medium text-gray-500 uppercase">Preview</div>
                <div className="text-sm text-gray-600 line-clamp-3">{template.body_template}</div>
              </div>
            </div>

            {template.metadata && template.metadata.week && (
              <div className="mt-4 p-2 bg-gray-50 rounded-md">
                <div className="text-xs text-gray-500">Week {template.metadata.week} - {template.metadata.phase}</div>
              </div>
            )}

            <div className="mt-4 flex space-x-2">
              <button className="btn-secondary text-xs">Edit</button>
              <button className="btn-primary text-xs">Use Template</button>
            </div>
          </div>
        ))}
        
        {templates.length === 0 && (
          <div className="col-span-full text-center py-12">
            <svg className="w-12 h-12 mx-auto text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No templates found</h3>
            <p className="text-gray-500 mb-4">Get started by creating your first email template</p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="btn-primary"
            >
              Create Your First Template
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmailTemplates;
